<template>
    <div :class="['structure-node', `structure-node--${node.kind}`, { 'is-root': depth === 0 }]">
        <div class="structure-node__row">
            <button
                v-if="isExpandable && !isForcedOpen"
                type="button"
                class="structure-node__toggle"
                :aria-expanded="isExpanded"
                :aria-label="isExpanded ? 'Свернуть' : 'Развернуть'"
                @click="tree.toggle(node.key)"
            >
                <span :class="['structure-node__chevron', { 'is-collapsed': !isExpanded }]" aria-hidden="true">▾</span>
            </button>
            <span v-else class="structure-node__toggle" aria-hidden="true"></span>

            <div class="structure-node__identity">
                <div class="structure-node__title-line">
                    <span v-if="kindLabel" :class="['structure-node__kind', `structure-node__kind--${node.kind}`]">{{ kindLabel }}</span>
                    <span class="structure-node__title" :title="node.title">
                        <HighlightText :text="node.title" :query="tree.query.value" />
                    </span>
                    <span v-if="node.entity" class="structure-node__number">#{{ node.entity.number }}</span>
                    <MarkupChip
                        v-if="node.kind === ENTITY_KIND.PARTNER"
                        :markup="node.entity.markup"
                        :editable="canEditMarkup"
                        @edit="editPartnerMarkup"
                    />
                </div>
                <span class="structure-node__summary">{{ summary }}</span>
            </div>

            <div class="structure-node__access">
                <StructureAccessChips
                    v-if="node.kind === ENTITY_KIND.PARTNER"
                    :kind="ENTITY_KIND.PARTNER"
                    :users="tree.agenciesByPartner.value.get(Number(node.entity.id)) || []"
                    :total-count="node.entity.access_count"
                    @open-users="tree.openUsers(node.entity.id)"
                />
                <StructureAccessChips
                    v-else-if="node.kind === ENTITY_KIND.ADVERTISER"
                    :kind="ENTITY_KIND.ADVERTISER"
                    :users="tree.clientsByAdvertiser.value.get(Number(node.entity.id)) || []"
                    @open-users="tree.openUsers(node.entity.partner_id)"
                />
            </div>

            <div class="structure-node__actions">
                <span v-if="tree.busyKey.value === node.key" class="structure-node__busy" role="status">Сохранение…</span>
                <ActionsMenu v-else-if="menuItems.length" :items="menuItems" @select="tree.runAction($event, node)" />
            </div>
        </div>

        <div v-if="isExpanded" class="structure-node__children">
            <StructureNode v-for="child in node.children" :key="child.key" :node="child" :depth="depth + 1" />
            <div v-if="node.campaigns.length" class="structure-node__campaigns">
                <p v-if="node.kind === ENTITY_KIND.ADVERTISER" class="structure-node__subheading">Кампании без бренда</p>
                <CampaignList
                    :campaigns="node.campaigns"
                    :partner="node.partner"
                    :query="tree.query.value"
                    @edit-markup="campaign => tree.editMarkup(MARKUP_TARGET.CAMPAIGN_RTB, campaign, node.partner)"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, inject } from 'vue';
import ActionsMenu from '../../ui/ActionsMenu.vue';
import CampaignList from './CampaignList.vue';
import HighlightText from './HighlightText.vue';
import MarkupChip from './MarkupChip.vue';
import StructureAccessChips from './StructureAccessChips.vue';
import { STRUCTURE_TREE_CONTEXT, campaignSummary, deleteBlockReason, hasContent } from './buildStructureTree';
import { ENTITY_KIND, ENTITY_LABEL, GROUP_KIND, MARKUP_TARGET, STRUCTURE_ACTION } from '../../../constants/structure';
import { canEditAnyMarkupLevel } from '../../../utils/markupUtils';
import { PLURAL, countLabel } from '../../../utils/plural';

const props = defineProps({
    node: { type: Object, required: true },
    depth: { type: Number, default: 0 },
});

const tree = inject(STRUCTURE_TREE_CONTEXT);

const isExpandable = computed(() => hasContent(props.node));
const isForcedOpen = computed(() => tree.isForcedOpen(props.node.key));
const isExpanded = computed(() => isExpandable.value && tree.isExpanded(props.node.key));

const kindLabel = computed(() => ENTITY_LABEL[props.node.kind]?.one || '');

const canEditMarkup = computed(() => props.node.kind === ENTITY_KIND.PARTNER && canEditAnyMarkupLevel(props.node.entity));
const editPartnerMarkup = () => tree.editMarkup(MARKUP_TARGET.PARTNER, props.node.entity, props.node.entity);

const summary = computed(() => {
    const { kind, stats } = props.node;
    if (kind === ENTITY_KIND.PARTNER || kind === GROUP_KIND.NO_PARTNER) {
        return stats.advertisers ? countLabel(stats.advertisers, PLURAL.ADVERTISER) : 'нет рекламодателей';
    }
    if (kind === ENTITY_KIND.ADVERTISER) {
        const brands = stats.brands ? countLabel(stats.brands, PLURAL.BRAND) : 'нет брендов';
        return `${brands} · ${campaignSummary(stats.campaigns)}`;
    }
    return campaignSummary(stats.campaigns);
});

const menuItems = computed(() => {
    const { kind, entity } = props.node;
    const abilities = entity?.abilities || {};
    const deleteReason = deleteBlockReason(props.node);

    return [
        kind === ENTITY_KIND.PARTNER && abilities.create_advertiser
            && { id: STRUCTURE_ACTION.CREATE_ADVERTISER, label: 'Добавить рекламодателя' },
        kind === ENTITY_KIND.ADVERTISER && abilities.create_brand
            && { id: STRUCTURE_ACTION.CREATE_BRAND, label: 'Добавить бренд' },
        abilities.update && { id: STRUCTURE_ACTION.EDIT, label: 'Редактировать' },
        canEditMarkup.value && { id: STRUCTURE_ACTION.EDIT_MARKUP, label: 'Наценки' },
        abilities.delete && {
            id: STRUCTURE_ACTION.DELETE,
            label: 'Удалить',
            danger: true,
            disabled: Boolean(deleteReason),
            hint: deleteReason,
        },
    ].filter(Boolean);
});
</script>

<style scoped lang="scss">
@use '../../../../scss/pages/admin/admin-tokens' as *;

.structure-node {
    &.is-root {
        border: 1px solid $admin-border-color;
        border-radius: $radius-lg;
        background: $admin-bg-primary;

        & + & { margin-top: $spacing-md; }

        > .structure-node__row {
            border-radius: $radius-lg $radius-lg 0 0;
            background: $admin-bg-secondary;

            .structure-node__title { font-weight: 600; font-size: $font-size-md; }
        }
    }

    &:not(.is-root) > .structure-node__row {
        border-top: 1px solid $admin-border-color;
    }
}

.structure-node__row {
    display: grid;
    grid-template-columns: 28px minmax(200px, 2fr) minmax(0, 2fr) auto;
    align-items: center;
    gap: $spacing-md;
    padding: $spacing-sm $spacing-lg $spacing-sm $spacing-xs;

    &:hover { background: $admin-bg-secondary; }

    @media (max-width: $breakpoint-md) {
        grid-template-columns: 28px minmax(0, 1fr);

        .structure-node__access,
        .structure-node__actions {
            grid-column: 2;
            justify-content: flex-start;
        }
    }
}

.structure-node__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: $radius-sm;
    background: none;
    color: $admin-text-muted;
    cursor: pointer;

    &:hover { background: $admin-bg-hover; color: $admin-text-primary; }
}

span.structure-node__toggle { cursor: default; }

.structure-node__chevron {
    display: inline-block;
    transition: transform 0.15s ease;

    &.is-collapsed { transform: rotate(-90deg); }
}

.structure-node__identity {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.structure-node__title-line {
    display: flex;
    align-items: baseline;
    gap: $spacing-sm;
    min-width: 0;
}

.structure-node__kind {
    flex-shrink: 0;
    padding: 0 $spacing-xs;
    border-radius: $radius-sm;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    &--partner { background: #e0e7ff; color: #3730a3; }
    &--advertiser { background: #fce7f3; color: #9d174d; }
    &--brand { background: #fef3c7; color: #92400e; }
}

.structure-node__title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    color: $admin-text-primary;
}

.structure-node__number {
    flex-shrink: 0;
    font-size: $font-size-xs;
    color: $admin-text-light;
}

.structure-node__summary {
    font-size: $font-size-xs;
    color: $admin-text-muted;
}

.structure-node__access {
    min-width: 0;
}

.structure-node__actions {
    display: flex;
    justify-content: flex-end;
}

.structure-node__busy {
    font-size: $font-size-sm;
    color: $admin-text-muted;
}

.structure-node__children {
    padding-left: $spacing-xl;

    @media (max-width: $breakpoint-md) {
        padding-left: $spacing-sm;
    }
}

.structure-node__campaigns {
    padding: $spacing-xs $spacing-lg $spacing-sm 40px;
    border-top: 1px solid $admin-border-color;
}

.structure-node__subheading {
    margin: $spacing-xs 0;
    font-size: $font-size-xs;
    font-weight: 600;
    color: $admin-text-muted;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
</style>
